'use client';

import { c as _c } from "react/compiler-runtime";
import DefaultAlertDialog from '@/components/DefaultAlertDialog';
import IconLeave from '@/components/assets/icon_leave.svg';
import { IFRAME_SEARCH_PARAM_KEYS } from '@/constants/iframe';
import { useCheckEnterRoomContext } from '@/context/CheckEnterRoomContext';
import { useUAContext } from '@/context/UAContext';
import { useEnableFull3d } from '@/hooks/useEnableFull3d';
import { usePathname, useRouter } from '@/i18n/navigation';
import { postMessageToMobileApp } from '@/lib/mobile-app-bridge';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { useTranslations } from 'next-intl';
import { useSearchParams } from 'next/navigation';
import { useMemo, useState } from 'react';
type Full3dExitButtonProps = {
  buttonClassName?: string;
  className?: string;
  dialogContainer?: HTMLElement | null;
  dialogPresentation?: 'default' | 'mobile-control-bar';
  iconClassName?: string;
};
export default function Full3dExitButton(t0) {
  const $ = _c(61);
  const {
    buttonClassName,
    className,
    dialogContainer,
    dialogPresentation: t1,
    iconClassName
  } = t0;
  const dialogPresentation = t1 === undefined ? "default" : t1;
  const t = useTranslations();
  const {
    roomData
  } = useCheckEnterRoomContext();
  const {
    isMobileApp
  } = useUAContext();
  const enableFull3d = useEnableFull3d();
  const [dialogOpen, setDialogOpen] = useState(false);
  const isMobileControlBarDialog = dialogPresentation === "mobile-control-bar";
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [portalContainer, setPortalContainer] = useState(null);
  let t2;
  if ($[0] !== isMobileControlBarDialog || $[1] !== searchParams) {
    const params = new URLSearchParams(searchParams.toString());
    params.delete(IFRAME_SEARCH_PARAM_KEYS.FULL3D);
    if (isMobileControlBarDialog) {
      params.delete(IFRAME_SEARCH_PARAM_KEYS.KIDS_MODE);
      params.delete(IFRAME_SEARCH_PARAM_KEYS.VRAM_LIMIT_PASSED);
    }
    t2 = params.toString();
    $[0] = isMobileControlBarDialog;
    $[1] = searchParams;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const query = t2;
  const exitUrl = query ? `${pathname}?${query}` : pathname;
  if (!enableFull3d || roomData.scene_type !== "standalone") {
    return null;
  }
  const t3 = isMobileControlBarDialog ? "contents" : undefined;
  let t4;
  if ($[3] !== t3) {
    t4 = <div ref={setPortalContainer} className={t3} />;
    $[3] = t3;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  let t6;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      opacity: 0
    };
    t6 = {
      opacity: 1
    };
    $[5] = t5;
    $[6] = t6;
  } else {
    t5 = $[5];
    t6 = $[6];
  }
  let t7;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      duration: 0.6,
      ease: [0.4, 0, 0.2, 1]
    };
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const t8 = className ?? "z-floating absolute right-5 bottom-4";
  let t9;
  if ($[8] !== t) {
    t9 = t("leave-room-modal.leave-title");
    $[8] = t;
    $[9] = t9;
  } else {
    t9 = $[9];
  }
  let t10;
  if ($[10] !== buttonClassName) {
    t10 = cn("group flex cursor-pointer items-center justify-center rounded-3xl bg-black/40 px-4 py-2 backdrop-blur-sm transition-colors duration-200 hover:bg-black/55", buttonClassName);
    $[10] = buttonClassName;
    $[11] = t10;
  } else {
    t10 = $[11];
  }
  let t11;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = () => setDialogOpen(true);
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== iconClassName) {
    t12 = cn("group-hover:text-vive-brand-50 size-6 shrink-0 text-[#D5E2F2] transition-[opacity,color] duration-200 group-hover:opacity-90", iconClassName);
    $[13] = iconClassName;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== t12) {
    t13 = <IconLeave className={t12} />;
    $[15] = t12;
    $[16] = t13;
  } else {
    t13 = $[16];
  }
  let t14;
  if ($[17] !== t10 || $[18] !== t13 || $[19] !== t9) {
    t14 = <button type="button" aria-label={t9} className={t10} onClick={t11}>{t13}</button>;
    $[17] = t10;
    $[18] = t13;
    $[19] = t9;
    $[20] = t14;
  } else {
    t14 = $[20];
  }
  let t15;
  if ($[21] !== t14 || $[22] !== t8) {
    t15 = <motion.div initial={t5} animate={t6} transition={t7} className={t8}>{t14}</motion.div>;
    $[21] = t14;
    $[22] = t8;
    $[23] = t15;
  } else {
    t15 = $[23];
  }
  const t16 = dialogContainer ?? (isMobileControlBarDialog ? undefined : portalContainer ?? undefined);
  let t17;
  if ($[24] !== t) {
    t17 = t("leave-room-modal.leave-title");
    $[24] = t;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  const t18 = isMobileControlBarDialog && "text-left text-sm leading-[22px] tracking-[0.2px]";
  let t19;
  if ($[26] !== t18) {
    t19 = cn("body1-omni-styles text-vive-primary-20 text-center md:text-sm md:leading-5.5", t18);
    $[26] = t18;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  if ($[28] !== t) {
    t20 = t("leave-room-modal.leave-world.message");
    $[28] = t;
    $[29] = t20;
  } else {
    t20 = $[29];
  }
  let t21;
  if ($[30] !== t19 || $[31] !== t20) {
    t21 = <p className={t19}>{t20}</p>;
    $[30] = t19;
    $[31] = t20;
    $[32] = t21;
  } else {
    t21 = $[32];
  }
  let t22;
  if ($[33] !== t) {
    t22 = t("leave-room-modal.leave-world.cancel");
    $[33] = t;
    $[34] = t22;
  } else {
    t22 = $[34];
  }
  let t23;
  if ($[35] !== t) {
    t23 = t("leave-room-modal.leave-world.confirm");
    $[35] = t;
    $[36] = t23;
  } else {
    t23 = $[36];
  }
  let t24;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = () => setDialogOpen(false);
    $[37] = t24;
  } else {
    t24 = $[37];
  }
  let t25;
  if ($[38] !== exitUrl || $[39] !== isMobileApp || $[40] !== router) {
    t25 = () => {
      setDialogOpen(false);
      if (isMobileApp && "flutterWebview" in window) {
        postMessageToMobileApp({
          feature: "web-back"
        });
        return;
      }
      router.push(exitUrl);
    };
    $[38] = exitUrl;
    $[39] = isMobileApp;
    $[40] = router;
    $[41] = t25;
  } else {
    t25 = $[41];
  }
  const t26 = isMobileControlBarDialog && "w-[312px] min-w-[312px] gap-6 p-6 md:max-h-none md:min-h-0 md:max-w-[312px] md:min-w-[312px] md:gap-6 md:p-6 [&>div:first-child]:gap-2";
  let t27;
  if ($[42] !== t26) {
    t27 = cn("md:max-h-fit md:min-h-fit md:max-w-[312px] md:min-w-[312px] md:gap-6 md:p-6 [&>div:first-child]:md:gap-2", t26);
    $[42] = t26;
    $[43] = t27;
  } else {
    t27 = $[43];
  }
  const t28 = isMobileControlBarDialog && "text-lg leading-[26px] tracking-[0.1px]";
  let t29;
  if ($[44] !== t28) {
    t29 = cn("md:text-lg md:leading-6.5 font-extrabold", t28);
    $[44] = t28;
    $[45] = t29;
  } else {
    t29 = $[45];
  }
  const t30 = isMobileControlBarDialog ? "left" : "center";
  let t31;
  if ($[46] !== dialogOpen || $[47] !== t16 || $[48] !== t17 || $[49] !== t21 || $[50] !== t22 || $[51] !== t23 || $[52] !== t25 || $[53] !== t27 || $[54] !== t29 || $[55] !== t30) {
    t31 = <DefaultAlertDialog open={dialogOpen} type="default" container={t16} title={t17} content={t21} cancelText={t22} actionText={t23} onCancel={t24} onAction={t25} className={t27} titleClassName={t29} buttonSize="small2" buttonContainerClassName="md:w-[264px]" contentAlign={t30} />;
    $[46] = dialogOpen;
    $[47] = t16;
    $[48] = t17;
    $[49] = t21;
    $[50] = t22;
    $[51] = t23;
    $[52] = t25;
    $[53] = t27;
    $[54] = t29;
    $[55] = t30;
    $[56] = t31;
  } else {
    t31 = $[56];
  }
  let t32;
  if ($[57] !== t15 || $[58] !== t31 || $[59] !== t4) {
    t32 = <>{t4}{t15}{t31}</>;
    $[57] = t15;
    $[58] = t31;
    $[59] = t4;
    $[60] = t32;
  } else {
    t32 = $[60];
  }
  return t32;
}